import AddRoomButton from 'components/Common/AddRoomButton'
import config from 'constants/config'
import noop from 'lib/function/noop'
import type { AdultLabel } from 'lib/offer/occupancyUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { forwardRef, memo, useCallback, useMemo } from 'react'
import type { OccupancyChildVariant } from './OccupancyItem'
import OccupancyItem from './OccupancyItem'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { useIntl } from 'react-intl'
import { formatSaleUnitLabel } from 'hooks/useSaleUnitFormatting'

const Container = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

function isOfferPageRooms(occupancies: Array<App.Occupants | App.RoomOccupants | OfferPageRoom>): occupancies is Array<OfferPageRoom> {
  return !!occupancies.length && ('id' in occupancies[0])
}

interface Props extends Utils.PropsWithDataAttributes {
  occupancies: Array<App.Occupants | App.RoomOccupants | OfferPageRoom>
  maxRooms?: number
  noServiceContact?: boolean
  capacities?: Array<App.RoomCapacity>
  maxChildAge?: number
  maxInfantAge?: number
  minInfantAge?: number
  maxTeenagerAge?: number
  saleUnit?: string
  onChange?: (occupancies: Array<App.Occupants | App.RoomOccupants | OfferPageRoom>) => void
  name?: string
  className?: string
  childrenAgeLabel?: string
  compact?: boolean
  enableMultiRoom?: boolean
  adultLabelType?: AdultLabel
  childVariant?: OccupancyChildVariant
  bundleOfferId?: string
  offerType?: App.OfferType
  offerId?: string
  disableSaleUnit?: boolean
  departureDate?: string
  removeAdultAgeCaption?: boolean
  /** Disable all "+" buttons to prevent adding more occupants */
  disableAddOccupants?: boolean
  /** Maximum total occupants allowed (excluding infants). When set, new rooms will adjust adults to fit within limit */
  maxOccupants?: number
  /** Guests included per room's price, as allowed per-category combinations. Rooms whose selected occupants exceed every combination show an extra-guests surcharge notice. Omit to disable. */
  includedGuests?: Array<App.PackageRoomIncludedGuests>
  allowPastPassengerNumber?: boolean
  isPastPassengerNumberLoading?: boolean
  pastPassengerFieldsRequired?: Array<string>
  personTitlesSupported?: Array<App.Cruises.BookingPersonTitles>
}

const OccupancyPicker = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    occupancies,
    capacities,
    maxChildAge,
    maxInfantAge,
    minInfantAge,
    maxTeenagerAge,
    saleUnit = 'room',
    onChange = noop,
    maxRooms,
    noServiceContact,
    name = 'occupants', // not user-visible - don't translate
    childrenAgeLabel: childrenAgeLabelProp,
    compact = false,
    enableMultiRoom,
    adultLabelType,
    childVariant: showChildren,
    bundleOfferId,
    offerType,
    offerId,
    disableSaleUnit,
    departureDate,
    removeAdultAgeCaption,
    disableAddOccupants,
    maxOccupants,
    includedGuests,
    allowPastPassengerNumber,
    isPastPassengerNumberLoading,
    pastPassengerFieldsRequired,
    personTitlesSupported,
    ...rest
  } = props

  const intl = useIntl()
  const childrenAgeLabel = childrenAgeLabelProp ?? intl.formatMessage({
    id: 'GBbQFyyy2xTvJgjpJfV6',
    defaultMessage: 'Age at time of check-in',
    description: 'Default label for children age selection in occupancy picker',
  })

  // Calculate current total occupants for max occupants logic
  const currentTotalOccupants = useMemo(() => {
    if (maxOccupants === undefined) return 0
    return sumUpOccupancies(occupancies)
  }, [occupancies, maxOccupants])

  // Calculate remaining capacity for new room
  const remainingCapacity = maxOccupants !== undefined ? maxOccupants - currentTotalOccupants : undefined

  // Check if adding a room is disabled (no remaining capacity)
  const isAddRoomDisabled = remainingCapacity !== undefined && remainingCapacity <= 0

  const onAddAnotherRoom = useCallback(() => {
    const defaultAdults = config.search.defaultOccupants.adults
    // If maxOccupants is set, adjust adults count to fit within remaining capacity
    const adjustedAdults = remainingCapacity !== undefined
      ? Math.min(defaultAdults, remainingCapacity)
      : defaultAdults

    const newRoomOccupants = {
      ...config.search.defaultOccupants,
      adults: Math.max(1, adjustedAdults), // At least 1 adult
    }
    const newRoom = isOfferPageRooms(occupancies) ? generateOfferPageRoom(newRoomOccupants) : newRoomOccupants
    const newOccupancies = [...occupancies, newRoom]
    onChange(newOccupancies)
  }, [occupancies, onChange, remainingCapacity])

  const onRemove = useCallback((index: number) => {
    const newOccupancies = [...occupancies]
    newOccupancies.splice(index, 1)
    onChange(newOccupancies)
  }, [occupancies, onChange])

  const onReset = useCallback((index: number) => {
    const newOccupancies = [...occupancies]
    newOccupancies[index] = { ...newOccupancies[index], ...config.search.defaultOccupants }
    onChange(newOccupancies)
  }, [occupancies, onChange])

  const onItemChange = useCallback((index: number, newOccupancy: App.Occupants) => {
    const newOccupancies = [...occupancies]
    newOccupancies[index] = { ...newOccupancies[index], ...newOccupancy }
    onChange(newOccupancies)
  }, [occupancies, onChange])

  const getTitle = useCallback((index: number) => {
    if (disableSaleUnit) {
      return undefined
    }
    if (bundleOfferId) {
      return intl.formatMessage({
        id: 'P6UhRJCBziX5z7hDbtqp',
        defaultMessage: 'Guests',
        description: 'Label for guests in occupancy picker in the occupancy or guest picker',
      })
    }
    return intl.formatMessage(
      {
        id: 'OW-cSNYOCGI9mS6N-BK3',
        defaultMessage: '{saleUnit} {number}',
        description: 'Room number label in occupancy picker on the hotel or room booking page',
      },
      {
        saleUnit: formatSaleUnitLabel(intl, saleUnit) ?? saleUnit,
        number: index + 1,
      },
    )
  }, [disableSaleUnit, bundleOfferId, saleUnit, intl])

  return <Container {...rest} direction="vertical" gap={8} tabletGap={12} ref={ref}>
    {occupancies.map((occupants, index) => <OccupancyItem
      smallView={compact}
      key={index}
      itemId={index}
      occupancy={occupants}
      title={getTitle(index)}
      showRemove={index > 0}
      onReset={onReset}
      onRemove={onRemove}
      minInfantAge={minInfantAge}
      maxInfantAge={maxInfantAge}
      maxChildAge={maxChildAge}
      maxTeenagerAge={maxTeenagerAge}
      onChange={onItemChange}
      capacities={capacities}
      childrenAgeLabel={childrenAgeLabel}
      name={name}
      adultLabelType={adultLabelType}
      childVariant={showChildren}
      offerType={offerType}
      offerId={offerId}
      departureDate={departureDate}
      removeAdultAgeCaption={removeAdultAgeCaption}
      disableAddOccupants={disableAddOccupants}
      includedGuests={includedGuests}
      allowPastPassengerNumber={allowPastPassengerNumber}
      isPastPassengerNumberLoading={isPastPassengerNumberLoading}
      pastPassengerFieldsRequired={pastPassengerFieldsRequired}
      personTitlesSupported={personTitlesSupported}
    />)}
    {enableMultiRoom && !isAddRoomDisabled && <Sheet background="normal-background" padding="8 16" tabletPadding={0}>
      <AddRoomButton
        maxRooms={maxRooms}
        saleUnit={saleUnit}
        roomsCount={occupancies.length}
        onClick={onAddAnotherRoom}
        noServiceContact={noServiceContact}
      />
    </Sheet>}
  </Container>
})
OccupancyPicker.displayName = 'OccupancyPicker'

export default memo(OccupancyPicker)
